<template>
    <section
        v-if="course"
        class="course"
        :class="capsule.is"
    >
        <Component
            :is="preview ? 'div' : 'main'"
            class="course-main"
        >
            <Screen
                ref="screen"
                :key="selectedScreen.id"
                :screen="selectedScreen"
                :course="_course"
                :selected="selectedSequence"
                :preview="preview"
                @screen:start="onScreenStart"
                @screen:end="onScreenEnd"
                @screen:complete="onScreenComplete"
                @quiz:answer="onQuizAnswer(selectedScreen.id, $event)"
                @quiz:retry="onQuizRetry(selectedScreen.id)"
                @next="navigateForward"
                @context="onScreenContext"
                @scroll:down="isNavHidden = true"
                @scroll:up="isNavHidden = false"
                @scroll:top="isNavHidden = false"
                @scroll:bottom="isNavHidden = false"
                @feedback="onFeedback"
            />
            <Navigation
                :course="_course"
                :selected="selectedSequence"
                :hide="isNavHidden"
                :preview="preview"
                :headless="headless"
                :intercom="intercom"
                @back="navigateBackward"
                @next="navigateForward"
                @upgrade="$emit('upgrade', $event)"
                @restart="selectedSequence = 0"
            />
        </Component>
        <ResumeCourse
            v-if="isResuming"
            :course="course"
            @reset="selectedSequence = 0"
            @close="isResuming = false"
        />
    </section>
</template>

<script>
import useTracking from '@/store/tracking';
import useLearnerState from '@/store/learner.author';

import { loadFont } from '@/store/theme';
import { useResponsive } from '@/store/responsive';
import { isCompletable } from '@/helpers/utilities';
import { startScreen, finishScreen } from '@/helpers/course';

import Txt from '@/components/form/txt';
import Modal from '@/components/utility/modal';
import Screen from '@/components/author/screen';
import Button from '@/components/utility/button';
import Clickable from '@/components/utility/clickable';
import ScrollArea from '@/components/utility/scroll-area';
import ProgressBar from '@/components/progress/progress-bar';
import Navigation from '@/components/author/course/navigation';
import ResumeCourse from '@/components/author/course/resume-course';

import Logo from '@/assets/CO_Logo_H_RGB.svg';
import LogoSymbol from '@/assets/CO_Symbol_RGB.svg';

export default {
    name: 'CoursePlayer',
    components: { Txt, Modal, Screen, Button, Clickable, ScrollArea, ProgressBar, Navigation, ResumeCourse, Logo, LogoSymbol },
    inject: ['$global'],
    props: {
        course: { type: Object, default: null },
        selected: { type: Number, default: 0 },

        preview: { type: Boolean, default: false },
        headless: { type: Boolean, default: false },
        intercom: { type: Boolean, default: false },
        disabled: { type: Boolean, default: false }
    },
    emits: ['next', 'commenced', 'complete', 'upgrade'],
    data() {
        return {
            selectedSequence: this.selected,
            maxProgress: 0,

            isResuming: false,
            isNavHidden: false,

            capsule: useResponsive('capsule')
        };
    },
    computed: {
        _course() {
            return useLearnerState.state.course
                || this.course;
        },
        startScreen() {
            return startScreen(this.course);
        },
        finishScreen() {
            return finishScreen(this.course, !this.$global.isLoggedIn, this.headless);
        },
        selectedScreen() {
            if (this.selectedSequence === this.course.screens.length + 1) return this.finishScreen;
            if (this.selectedSequence === 0) return this.startScreen;
            return this.course.screens.find(screen => screen.sequence === this.selectedSequence);
        },
        progress() {
            if (!this.course) return 0;
            const total = this.course.screens.length;
            const current = this.selectedSequence;
            const progress = Math.round((current / total) * 100);
            return Math.min(progress, 100);
        },
        lockToScreen() {
            if (!this.course) return -1;
            if (!(this.course.screens && Array.isArray(this.course.screens))) return -1;
            if (this.preview) return -1;
            const index = this.course.screens.findIndex(screen => isCompletable(screen.type && screen.type.completable) && !screen.completed);
            if (~index) return index;
            return this.course.screens.length;
        },
        lockToScreenSequence() {
            if (!this.course) return 0;
            return this.course.screens[this.lockToScreen]
                && this.course.screens[this.lockToScreen].sequence
                || this.course.screens.length + 2;
        }
    },
    watch: {
        course: {
            handler(to, from) {
                if (this.preview) return;
                if (to && !from) {
                    useTracking.state.isScorm = this.course.scorm;
                    useTracking.connect(this.$config.public.apiURL, to);
                }
            },
            deep: true,
            immediate: true
        },
        'course.scorm': {
            handler(to) {
                useTracking.state.isScorm = to;
            },
            immediate: true
        },
        'course.theme.font': {
            handler(to) {
                if (to === 'Poppins') return;
                loadFont(to);
            },
            immediate: true
        },
        selectedSequence(to) {
            if (to > this.course.screens.length) {
                this.$emit('complete');
                this.postMessage({
                    type: 'course',
                    event: 'complete',
                    data: { id: this.course.id, progress: 100 }
                });
                if (this.course.scorm) {
                    this.postMessage({
                        action: 'unitComplete',
                        check: 100,
                        completed: true,
                        data: this.course.screens.map(() => 1).join(','),
                        proceed: true,
                        progress_percent: 100,
                        refresh_token: false
                    });
                }
            }
        },
        progress(to) {
            if (to > this.maxProgress) this.maxProgress = to;
        },
        maxProgress(to, from) {
            if (from === 0) {
                this.$emit('commenced');
                this.postMessage({
                    type: 'course',
                    event: 'commenced',
                    data: { id: this.course.id }
                });
            }
            this.postMessage({
                type: 'course',
                event: 'progress',
                data: { id: this.course.id, progress: to }
            });
        }
    },
    mounted() {
        useLearnerState.setCourse(this.course);
        this.postMessage({
            type: 'course',
            event: 'start',
            data: { id: this.course.id, progress: this.course.progressPercent }
        });
        
        this.$nextTick(() => {
            if (this.preview) return;
            const startingSequence = this.getStartSequence();
            this.goToScreen(startingSequence);
            this.isResuming = startingSequence > 0;
        });
    },
    methods: {
        getStartSequence() {
            if (!(this.course && this.course.screens && this.course.screens.length)) {
                return 0;
            }

            if (this.course.screens.every(screen => !screen.completed)) {
                return 0;
            }

            const firstIncompleteScreen = this.course.screens.find(screen => !screen.completed);
            if (firstIncompleteScreen) {
                return firstIncompleteScreen.sequence;
            }

            return this.course.screens.length + 1;
        },
        goToScreen(to) {
            const finish = this.course.screens.length + 1;
            if (to < 0 || to > finish) return;
            if (to === this.selectedSequence) return;
            if (to > this.lockToScreenSequence) return;
            this.selectedSequence = to;
        },
        onFeedback(feedback) {
            this.postMessage({
                type: 'course',
                event: 'feedback',
                data: { id: this.course.id, feedback }
            });
        },
        onScreenStart(screenId = null) {
            if (this.preview) return;
            useTracking.start(this.course, screenId);
            this.postMessage({
                type: 'screen',
                event: 'start',
                data: { id: screenId }
            });
        },
        onScreenContext(screenId, context) {
            useTracking.progress(this.course, screenId, context);
        },
        onScreenEnd(screenId = null, elapsedTime = 0) {
            if (this.preview) return;
            useTracking.end(this.course, screenId, elapsedTime);
            this.postMessage({
                type: 'screen',
                event: 'end',
                data: { id: screenId }
            });
        },
        onScreenComplete(screenId = null, context = {}) {
            const screen = this.course.screens.find(screen => screen.id === screenId);
            if (!screen) return;
            useLearnerState.setScreen(screen.id, { completed: true });
            if (this.preview) return;
            if (typeof context !== 'object') context = {};
            useTracking.complete(this.course, screenId, false, context);
            this.postMessage({
                type: 'screen',
                event: 'complete',
                data: { id: screenId, ...context }
            });
        },
        onQuizAnswer(screenId, answer) {
            if (this.preview) return;
            this.postMessage({
                type: 'screen',
                event: 'answer',
                data: {
                    id: screenId,
                    correct: answer.correct
                }
            });
            useTracking.answer(this.course, screenId, answer.correct, answer);
        },
        onQuizRetry(screenId) {
            if (this.preview) return;
            this.postMessage({
                type: 'screen',
                event: 'retry',
                data: { id: screenId }
            });
            useTracking.retry(this.course, screenId);
        },
        navigateBackward() {
            let navigated = false;
            if (this.$refs.screen && this.$refs.screen.previous) navigated = this.$refs.screen.previous();
            if (navigated === false) this.goToScreen(this.selectedSequence - 1);
        },
        navigateForward() {
            let navigated = false;
            if (this.$refs.screen && this.$refs.screen.next) navigated = this.$refs.screen.next();
            if (navigated === false) this.goToScreen(this.selectedSequence + 1);
            this.$emit('next');
        },
        postMessage(data) {
            if (this.preview) return;
            if (window.self !== window.top) window.parent.postMessage(JSON.stringify(data), '*');
        }
    },
};
</script>

<style lang="less">
section.course {
    --full-width: 100vw;
    --full-height: 100dvh;
    width: 100%;
    overflow: hidden;
    background: var(--asphalt);
    .course-main {
        width: var(--full-width);
        height: var(--full-height);
        margin: auto;
        transition: width 0.35s var(--curve);
        background-color: #FFF;
        article.screen {
            height: var(--full-height);
        }
    }
    &.mobile {
        .course-main {
            overflow: hidden;
            scroll-behavior: unset;
            article.screen {
                position: relative;
                z-index: 1;
            }
        }
    }
}
</style>
